import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError, money } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import CodigoTributacaoNacionalCombobox from "../components/CodigoTributacaoNacionalCombobox";
import MunicipioCombobox from "../components/MunicipioCombobox";
import PaisCombobox from "../components/PaisCombobox";
import { toast } from "sonner";
import {
  FileCheck2, Send, CheckCircle2, AlertTriangle, Circle, Info, X,
  LayoutDashboard, Users, UserPlus, Wrench, Percent, DollarSign, FileText,
  ClipboardCheck, Building2, ShieldCheck, CalendarDays, Layers, Clock, Lock,
  RefreshCw,
} from "lucide-react";

// Editor completo da NFS-e/DPS — DELTA visual: a mesma implementação
// funcional (uma página) reorganizada como JANELA GRANDE / modal quase
// fullscreen, com abas no topo (uma seção por vez) + resumo lateral +
// rodapé fixo de ações. NADA da lógica de negócio/validação muda: toda a
// obrigatoriedade real continua no backend (dps_builder.build_dps_xml);
// aqui as regras de "quando aparece o quê" são SÓ de apresentação.

const SECOES = [
  { id: "geral", nome: "Geral" },
  { id: "prestador", nome: "Prestador" },
  { id: "tomador", nome: "Tomador" },
  { id: "destinatario", nome: "Destinatário" },
  { id: "intermediario", nome: "Intermediário" },
  { id: "servico", nome: "Serviço" },
  { id: "tributacao", nome: "Tributação" },
  { id: "ibscbs", nome: "IBS/CBS" },
  { id: "complementares", nome: "Complementares" },
  { id: "especiais", nome: "Situações especiais" },
  { id: "revisao", nome: "Revisão" },
];

// Abas visuais (uma seção por vez) — cada aba agrega uma ou mais das seções
// oficiais acima. `statusKeys` reaproveita o status já calculado por seção.
const TABS = [
  { id: "visao_geral", nome: "Visão geral", Icon: LayoutDashboard, statusKeys: ["geral", "prestador"] },
  { id: "tomador", nome: "Tomador", Icon: Users, statusKeys: ["tomador"] },
  { id: "intermediario", nome: "Intermediário", Icon: UserPlus, statusKeys: ["intermediario"] },
  { id: "servico", nome: "Serviço", Icon: Wrench, statusKeys: ["servico"] },
  { id: "tributacao", nome: "Tributação", Icon: Percent, statusKeys: ["tributacao"] },
  { id: "valores", nome: "Valores", Icon: DollarSign, statusKeys: [] },
  { id: "complementares", nome: "Complementares", Icon: FileText, statusKeys: ["complementares", "especiais"] },
  { id: "revisao", nome: "Revisão", Icon: ClipboardCheck, statusKeys: ["revisao"] },
];

const centsToStr = (c) => (c === null || c === undefined ? "" : (c / 100).toFixed(2));
const strToCents = (s) => (s === "" || s === null || s === undefined ? null : Math.round(parseFloat(s) * 100));

const ENDERECO_VAZIO = { tipo: "nacional", logradouro: "", numero: "", complemento: "", bairro: "", codigo_municipio_ibge: "", cep: "", pais: "", codigo_postal_exterior: "", cidade_exterior: "", estado_provincia_exterior: "" };
const TOMA_EXTRA_VAZIO = { tipo: "brasil", nif: "", motivo_nao_nif: null, inscricao_municipal: "", telefone: "", email: "", endereco: null };
const INTERM_VAZIO = { presente: false, tipo_documento: "cnpj", cnpj: "", cpf: "", nif: "", motivo_nao_nif: null, xnome: "", inscricao_municipal: "", telefone: "", email: "", endereco: null };

const FORM_DEFAULTS = {
  competencia: "",
  codigo_municipio_ibge: "", codigo_servico_nacional: "", codigo_tributacao_municipal: "", codigo_nbs: "",
  descricao_servico: "",
  local_prestacao_tipo: "nacional", pais_prestacao: "",
  toma_extra: null, interm: null,
  comex_presente: false, comex_modo_prestacao: null, comex_vinculo_prestacao: null, comex_moeda: "",
  comex_valor_servico_moeda_centavos: null, comex_mecanismo_fomento_prestador: "", comex_mecanismo_fomento_tomador: "",
  comex_movimentacao_temp_bens: null, comex_numero_di: "", comex_numero_re: "", comex_enviar_mdic: null,
  valor_recebido_intermediario_centavos: null, desconto_incondicionado_centavos: null, desconto_condicionado_centavos: null,
  tributacao_issqn: "tributavel", pais_resultado_exportacao: "", tipo_imunidade: null,
  retencao_issqn: "nao_retido", aliquota_issqn: "",
  suspensao_exigibilidade: false, tipo_suspensao: null, numero_processo_suspensao: "",
  beneficio_municipal: false, numero_beneficio_municipal: "", valor_reducao_base_beneficio_centavos: null, percentual_reducao_base_beneficio: "",
  tributacao_federal_presente: false, pis_cofins_cst: "", pis_cofins_base_centavos: null,
  pis_cofins_aliquota_pis: "", pis_cofins_aliquota_cofins: "", pis_cofins_valor_pis_centavos: null,
  pis_cofins_valor_cofins_centavos: null, pis_cofins_tipo_retencao: null,
  valor_retencao_cp_centavos: null, valor_retencao_irrf_centavos: null, valor_retencao_csll_centavos: null,
  doc_responsabilidade_tecnica: "", documento_referencia: "", numero_pedido: "", informacoes_complementares: "",
};

function statusIcon(status) {
  if (status === "ok") return <CheckCircle2 size={15} className="text-emerald-600" />;
  if (status === "alerta") return <AlertTriangle size={15} className="text-amber-500" />;
  return <Circle size={15} className="text-slate-300" />;
}

// Cabeçalho de seção reutilizável (ícone arredondado + título + descrição).
function SectionHead({ icon: Ic, title, desc }) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <Ic size={20} />
      </div>
      <div className="min-w-0">
        <div className="font-heading font-semibold text-slate-900 leading-tight">{title}</div>
        {desc && <div className="text-xs text-slate-500 mt-0.5">{desc}</div>}
      </div>
    </div>
  );
}

export default function NotaFiscalEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [info, setInfo] = useState({});
  const [form, setForm] = useState(FORM_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [emitting, setEmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("visao_geral");
  const [clients, setClients] = useState([]);
  const [resumoOpen, setResumoOpen] = useState(false);
  const [municipais, setMunicipais] = useState(null);
  const [municipaisLoading, setMunicipaisLoading] = useState(false);

  const readOnly = doc && (doc.status === "emitida" || doc.status === "cancelada");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/nfse/${id}`);
      setDoc(data);
      setInfo(data.dps_fields_info || {});
      const f = data.dps_fields || {};
      setForm({
        ...FORM_DEFAULTS,
        ...Object.fromEntries(Object.entries(f).filter(([k]) => k in FORM_DEFAULTS)),
        toma_extra: f.toma_extra || null,
        interm: f.interm || null,
        aliquota_issqn: f.aliquota_issqn ?? "",
        percentual_reducao_base_beneficio: f.percentual_reducao_base_beneficio ?? "",
        pis_cofins_aliquota_pis: f.pis_cofins_aliquota_pis ?? "",
        pis_cofins_aliquota_cofins: f.pis_cofins_aliquota_cofins ?? "",
      });
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/notas-fiscais");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Reaproveita o endpoint EXISTENTE /clients (mesmo padrão de NotasFiscais)
  // só para exibir o nome do tomador no resumo lateral — somente leitura.
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  // Parâmetros municipais (item 9 do pedido) — consulta o snapshot já
  // SALVO no servidor (não o form ainda não persistido). Reconsulta a cada
  // `doc` novo (carga inicial + depois de qualquer salvar/validar bem
  // sucedido), nunca a cada tecla digitada — evita bater na API a cada
  // render (item 7).
  const carregarParametrosMunicipais = async (refresh = false) => {
    setMunicipaisLoading(true);
    try {
      const { data } = await api.get(`/nfse/${id}/parametros-municipais`, refresh ? { params: { refresh: true } } : undefined);
      setMunicipais(data);
    } catch (e) {
      setMunicipais({ verificado: false, motivo: "Não foi possível consultar os parâmetros municipais neste momento." });
    } finally {
      setMunicipaisLoading(false);
    }
  };
  useEffect(() => {
    if (doc?.id) carregarParametrosMunicipais(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setToma = (patch) => setForm((f) => ({ ...f, toma_extra: { ...(f.toma_extra || TOMA_EXTRA_VAZIO), ...patch } }));
  const setTomaEnd = (patch) => setForm((f) => ({ ...f, toma_extra: { ...(f.toma_extra || TOMA_EXTRA_VAZIO), endereco: { ...((f.toma_extra || {}).endereco || ENDERECO_VAZIO), ...patch } } }));
  const setInterm = (patch) => setForm((f) => ({ ...f, interm: { ...(f.interm || INTERM_VAZIO), ...patch } }));
  const setIntermEnd = (patch) => setForm((f) => ({ ...f, interm: { ...(f.interm || INTERM_VAZIO), endereco: { ...((f.interm || {}).endereco || ENDERECO_VAZIO), ...patch } } }));

  const salvar = async ({ silent } = {}) => {
    setSaving(true);
    try {
      const body = {
        ...form,
        aliquota_issqn: form.aliquota_issqn === "" ? null : Number(form.aliquota_issqn),
        percentual_reducao_base_beneficio: form.percentual_reducao_base_beneficio === "" ? null : Number(form.percentual_reducao_base_beneficio),
        pis_cofins_aliquota_pis: form.pis_cofins_aliquota_pis === "" ? null : Number(form.pis_cofins_aliquota_pis),
        pis_cofins_aliquota_cofins: form.pis_cofins_aliquota_cofins === "" ? null : Number(form.pis_cofins_aliquota_cofins),
        codigo_tributacao_municipal: form.codigo_tributacao_municipal || null,
        codigo_nbs: form.codigo_nbs || null,
        pais_prestacao: form.pais_prestacao || null,
        pais_resultado_exportacao: form.pais_resultado_exportacao || null,
        numero_processo_suspensao: form.numero_processo_suspensao || null,
        numero_beneficio_municipal: form.numero_beneficio_municipal || null,
        pis_cofins_cst: form.pis_cofins_cst || null,
        comex_moeda: form.comex_moeda || null,
        comex_mecanismo_fomento_prestador: form.comex_mecanismo_fomento_prestador || null,
        comex_mecanismo_fomento_tomador: form.comex_mecanismo_fomento_tomador || null,
        comex_numero_di: form.comex_numero_di || null,
        comex_numero_re: form.comex_numero_re || null,
      };
      const { data } = await api.put(`/nfse/${id}/dps-fields`, body);
      setDoc(data);
      setInfo(data.dps_fields_info || {});
      if (!silent) toast.success("Rascunho salvo — valide a DPS antes de emitir");
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const validar = async () => {
    const ok = await salvar({ silent: true });
    if (!ok) return;
    setValidating(true);
    try {
      const { data } = await api.post(`/nfse/${id}/validar`);
      setDoc(data);
      if (data.validation_errors?.length) toast.warning(`DPS com ${data.validation_errors.length} pendência(s) — veja a Revisão`);
      else toast.success("DPS válida (estrutura XSD ok)");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setValidating(false); }
  };

  const emitir = async () => {
    setEmitting(true);
    try {
      await api.post(`/nfse/${id}/emitir`);
      toast.success("NFS-e emitida");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEmitting(false); }
  };

  // Status por seção — só apresentação (a regra real está no backend).
  const secaoStatus = useMemo(() => {
    const s = {};
    s.geral = "ok";
    s.prestador = form.codigo_municipio_ibge ? "ok" : "alerta";
    s.tomador = "ok";
    s.destinatario = "ok";
    s.intermediario = !form.interm?.presente || (form.interm?.xnome && (form.interm?.cnpj || form.interm?.cpf || form.interm?.nif || form.interm?.motivo_nao_nif !== null)) ? "ok" : "alerta";
    s.servico = form.codigo_servico_nacional ? "ok" : "alerta";
    s.tributacao = form.tributacao_issqn === "exportacao" && !form.pais_resultado_exportacao ? "alerta"
      : form.tributacao_issqn === "imunidade" && !form.tipo_imunidade ? "alerta"
      : form.suspensao_exigibilidade && !(form.tipo_suspensao && form.numero_processo_suspensao) ? "alerta" : "ok";
    s.ibscbs = "ok";
    s.complementares = "ok";
    s.especiais = form.comex_presente && !(form.comex_modo_prestacao && form.comex_vinculo_prestacao && form.comex_moeda) ? "alerta" : "ok";
    s.revisao = doc?.validation_errors?.length ? "alerta" : (doc?.status === "validada" || doc?.status === "emitida") ? "ok" : "pendente";
    return s;
  }, [form, doc]);

  const tabStatusOf = (t) => {
    if (!t.statusKeys.length) return "ok";
    const vals = t.statusKeys.map((k) => secaoStatus[k]);
    if (vals.some((v) => v === "alerta")) return "alerta";
    if (vals.some((v) => v === "pendente")) return "pendente";
    return "ok";
  };

  const naoRevisao = TABS.filter((t) => t.id !== "revisao");
  const filledCount = naoRevisao.filter((t) => tabStatusOf(t) === "ok").length;
  const progressPct = Math.round((filledCount / naoRevisao.length) * 100);

  if (loading) return <div className="p-8 text-center text-slate-400">Carregando…</div>;
  if (!doc) return null;

  const valorServico = doc.valor || 0;
  const descontoIncond = (form.desconto_incondicionado_centavos || 0) / 100;
  const descontoCond = (form.desconto_condicionado_centavos || 0) / 100;
  const valorLiquido = valorServico - descontoIncond - descontoCond;
  const valorIssqnEstimado = form.aliquota_issqn !== "" && form.aliquota_issqn !== null
    ? (valorServico * Number(form.aliquota_issqn)) / 100 : null;

  const cli = clients.find((c) => c.id === doc.cliente_id);
  const tomadorNome = cli?.nome_fantasia || cli?.razao_social || "—";
  const ambienteLabel = doc.ambiente === "producao" ? "Produção" : "Produção Restrita";
  const statusLabel = doc.status === "rascunho" ? "Rascunho" : doc.status === "validada" ? "Validada" : doc.status === "emitida" ? "Emitida" : doc.status === "cancelada" ? "Cancelada" : doc.status;
  const statusBadgeClass = doc.status === "emitida" ? "bg-emerald-100 text-emerald-800" : doc.status === "validada" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-600";
  const podeEmitir = doc.status === "validada" && !readOnly;

  const competenciaFmt = form.competencia
    ? form.competencia.split("-").reverse().join("/")
    : "—";

  // ---- Resumo lateral (reutilizado no desktop e no bloco recolhível) ----
  const renderResumo = () => (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <FileText size={16} className="text-primary" />
          <div className="font-heading font-semibold text-slate-900 text-sm">Resumo da NFS-e</div>
        </div>
        <dl className="space-y-3 text-sm">
          <div className="flex items-start gap-2.5">
            <Users size={15} className="text-slate-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <dt className="text-[11px] uppercase tracking-wide text-slate-400">Cliente (Tomador)</dt>
              <dd className="font-medium text-slate-800 break-words leading-snug">{tomadorNome}</dd>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <CalendarDays size={15} className="text-slate-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <dt className="text-[11px] uppercase tracking-wide text-slate-400">Competência</dt>
              <dd className="font-medium text-slate-800">{competenciaFmt}</dd>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <Layers size={15} className="text-slate-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <dt className="text-[11px] uppercase tracking-wide text-slate-400">Ambiente</dt>
              <dd className="font-medium text-slate-800">{ambienteLabel}</dd>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <Clock size={15} className="text-slate-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <dt className="text-[11px] uppercase tracking-wide text-slate-400">Status</dt>
              <dd><Badge className={statusBadgeClass}>{statusLabel}</Badge></dd>
            </div>
          </div>
        </dl>
      </Card>

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={16} className="text-primary" />
          <div className="font-heading font-semibold text-slate-900 text-sm">Progresso da NFS-e</div>
        </div>
        <div className="text-xs text-slate-500 mb-3">{filledCount} de {naoRevisao.length} seções preenchidas</div>
        <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden mb-4">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progressPct}%` }} />
        </div>
        <div className="space-y-0.5">
          {TABS.map((t) => {
            const st = tabStatusOf(t);
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`w-full flex items-center gap-2 text-sm rounded-md px-2 py-1.5 text-left transition ${isActive ? "bg-primary/10 text-primary font-medium" : "hover:bg-slate-50 text-slate-600"}`}
                data-testid={`nfe-progress-${t.id}`}
              >
                {statusIcon(st)}
                <span className="truncate">{t.nome}</span>
              </button>
            );
          })}
        </div>
      </Card>

      <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-3 flex items-start gap-2 text-xs text-blue-800">
        <Info size={14} className="mt-0.5 shrink-0" />
        <span>Após o preenchimento, valide a DPS para verificar possíveis inconsistências antes de emitir a NFS-e.</span>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-900/40 backdrop-blur-sm" data-testid="nfe-editor-modal">
      <div className="flex-1 flex flex-col min-h-0 bg-slate-50 sm:m-3 md:m-4 sm:rounded-2xl shadow-2xl ring-1 ring-slate-200 overflow-hidden">

        {/* ===== HEADER ===== */}
        <div className="shrink-0 bg-white border-b border-slate-200 px-4 md:px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              {/* Marca LOOTUS — mesmo tratamento do Login (asset/estilo
                  existente; nada é recriado/substituído). */}
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center font-heading font-black text-sm text-white" style={{ background: "linear-gradient(135deg, #3B82F6, #1D4ED8)" }}>L</div>
                <span className="font-heading font-black tracking-tight text-slate-900">LOOTUS</span>
              </div>
              <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-900">Editar NFS-e / DPS</h1>
              <p className="text-xs md:text-sm text-slate-500 mt-1 max-w-2xl">
                Revise e ajuste os dados da nota antes da validação e emissão. Todas as informações são enviadas
                para a prefeitura através do padrão NFS-e / DPS.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="hidden sm:flex items-center gap-2">
                <Badge className={statusBadgeClass}>{statusLabel}</Badge>
              </div>
              <button
                type="button"
                onClick={() => navigate("/notas-fiscais")}
                className="w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
                aria-label="Fechar"
                data-testid="nfe-fechar-x"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* ===== BARRA DE ABAS ===== */}
        <div className="shrink-0 bg-white border-b border-slate-200 px-2 md:px-4 overflow-x-auto">
          <div className="flex items-center gap-1 min-w-max">
            {TABS.map((t) => {
              const isActive = activeTab === t.id;
              const Ic = t.Icon;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTab(t.id)}
                  className={`relative flex items-center gap-2 px-3 md:px-4 py-3 text-sm whitespace-nowrap transition border-b-2 ${isActive ? "border-primary text-primary font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                  data-testid={`nfe-tab-${t.id}`}
                >
                  <Ic size={16} />
                  {t.nome}
                </button>
              );
            })}
          </div>
        </div>

        {readOnly && (
          <div className="shrink-0 px-4 md:px-6 pt-4">
            <Card className="p-3 bg-slate-50 border-slate-200 flex items-center gap-2 text-sm text-slate-700">
              <Info size={16} className="shrink-0" />
              NFS-e {doc.status} — não pode mais ser editada aqui. Correções só via evento oficial (cancelamento/substituição).
            </Card>
          </div>
        )}

        {/* ===== BODY (scroll) ===== */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="max-w-[1400px] mx-auto w-full px-4 md:px-6 py-5 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-5">
            <div className="min-w-0 space-y-5">

              {/* Resumo recolhível (só < xl) */}
              <div className="xl:hidden">
                <button
                  type="button"
                  onClick={() => setResumoOpen((v) => !v)}
                  className="w-full flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700"
                  data-testid="nfe-resumo-toggle"
                >
                  <span className="flex items-center gap-2"><FileText size={15} className="text-primary" /> Resumo da NFS-e</span>
                  <span className="text-xs text-slate-400">{resumoOpen ? "Ocultar" : "Mostrar"}</span>
                </button>
                {resumoOpen && <div className="mt-3">{renderResumo()}</div>}
              </div>

              {/* ===================== VISÃO GERAL ===================== */}
              {activeTab === "visao_geral" && (
                <>
                  <Card className="p-5 md:p-6">
                    <SectionHead icon={LayoutDashboard} title="Informações gerais" desc="Dados de identificação da DPS e competência da nota." />
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><Label className="text-xs">Ambiente</Label><div className="mt-1.5">{ambienteLabel}</div></div>
                      <div><Label className="text-xs">Série / Número DPS</Label><div className="mt-1.5">{doc.serie}/{doc.ndps}</div></div>
                      <div><Label className="text-xs">Emitente da DPS</Label><div className="mt-1.5">Prestador</div></div>
                      <div>
                        <Label className="text-xs">Competência</Label>
                        <Input type="date" disabled={readOnly} value={form.competencia || ""} onChange={(e) => set({ competencia: e.target.value })} data-testid="nfe-competencia" />
                      </div>
                    </div>
                  </Card>

                  <Card className="p-5 md:p-6">
                    <SectionHead icon={Building2} title="Prestador / Emitente" desc="Vem da configuração fiscal da empresa — não exige redigitação aqui." />
                    <p className="text-xs text-slate-500 mb-3">
                      Para mudar CNPJ/inscrição/regime, use <Link to="/configuracoes" className="underline">Configurações → Fiscal/NFS-e</Link> (afeta só notas futuras).
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div><Label className="text-xs">CNPJ/CPF</Label><div className="mt-1.5 font-mono">{form.cnpj || form.cpf || doc.dps_fields?.cnpj || doc.dps_fields?.cpf || "—"}</div></div>
                      <div><Label className="text-xs">Inscrição Municipal</Label><div className="mt-1.5">{doc.dps_fields?.inscricao_municipal || "—"}</div></div>
                      <div><Label className="text-xs">Município</Label><div className="mt-1.5">{info.codigo_municipio_ibge_info?.exibicao || doc.dps_fields?.codigo_municipio_ibge || "—"}</div></div>
                      <div><Label className="text-xs">Simples Nacional</Label><div className="mt-1.5">{doc.dps_fields?.opcao_simples_nacional || "—"}</div></div>
                      <div><Label className="text-xs">Regime especial</Label><div className="mt-1.5">{doc.dps_fields?.regime_especial_tributacao || "0"}</div></div>
                    </div>
                  </Card>

                  <Card className="p-5 md:p-6">
                    <SectionHead icon={Info} title="Destinatário" desc="Grupo não aplicável ao leiaute padrão da DPS." />
                    <p className="text-xs text-slate-500 flex items-start gap-2">
                      <Info size={14} className="mt-0.5 shrink-0" />
                      O leiaute v1.01 da NFS-e Nacional não define um grupo “Destinatário” distinto do Tomador para a
                      DPS padrão — esse conceito só existe dentro do grupo IBS/CBS (RTC), que está fora de escopo nesta
                      fase (ver aba Tributação). Nenhum campo foi inventado aqui.
                    </p>
                  </Card>
                </>
              )}

              {/* ===================== TOMADOR ===================== */}
              {activeTab === "tomador" && (
                <Card className="p-5 md:p-6">
                  <SectionHead icon={Users} title="Tomador / Adquirente" desc="Identidade vem do cadastro do cliente da fatura. Os campos abaixo complementam a DPS." />
                  <p className="text-xs text-slate-500 mb-4">
                    Identidade (CNPJ/CPF/nome) vem sempre do cadastro do cliente da fatura — nunca editável aqui,
                    para não emitir para uma pessoa diferente de quem contratou.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <Label className="text-xs">Tipo</Label>
                      <Select disabled={readOnly} value={form.toma_extra?.tipo || "brasil"} onValueChange={(v) => setToma({ tipo: v })}>
                        <SelectTrigger data-testid="nfe-toma-tipo"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="brasil">Brasil</SelectItem>
                          <SelectItem value="exterior">Exterior</SelectItem>
                          <SelectItem value="nao_informado">Não informado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Inscrição Municipal</Label><Input disabled={readOnly} value={form.toma_extra?.inscricao_municipal || ""} onChange={(e) => setToma({ inscricao_municipal: e.target.value })} data-testid="nfe-toma-im" /></div>
                    <div><Label className="text-xs">Telefone</Label><Input disabled={readOnly} value={form.toma_extra?.telefone || ""} onChange={(e) => setToma({ telefone: e.target.value })} data-testid="nfe-toma-telefone" /></div>
                    <div className="md:col-span-2"><Label className="text-xs">E-mail (substitui o do cadastro do cliente, se informado)</Label><Input disabled={readOnly} value={form.toma_extra?.email || ""} onChange={(e) => setToma({ email: e.target.value })} data-testid="nfe-toma-email" /></div>

                    {form.toma_extra?.tipo === "exterior" && (
                      <>
                        <div><Label className="text-xs">NIF (identificação fiscal no exterior)</Label><Input disabled={readOnly} value={form.toma_extra?.nif || ""} onChange={(e) => setToma({ nif: e.target.value })} data-testid="nfe-toma-nif" /></div>
                        <div>
                          <Label className="text-xs">Motivo de ausência de NIF (se não houver NIF)</Label>
                          <Select disabled={readOnly} value={form.toma_extra?.motivo_nao_nif ?? "__none"} onValueChange={(v) => setToma({ motivo_nao_nif: v === "__none" ? null : v })}>
                            <SelectTrigger data-testid="nfe-toma-motivo-nif"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">—</SelectItem>
                              <SelectItem value="0">Não informado na nota de origem</SelectItem>
                              <SelectItem value="1">Dispensado do NIF</SelectItem>
                              <SelectItem value="2">Não exigência do NIF</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}

                    <Separator className="md:col-span-3 my-1" />
                    <div className="md:col-span-3 text-xs font-medium text-slate-600">Endereço</div>
                    {form.toma_extra?.tipo === "exterior" ? (
                      <>
                        <div><Label className="text-xs">País</Label><PaisCombobox value={form.toma_extra?.endereco?.pais || ""} onChange={(c) => setTomaEnd({ tipo: "exterior", pais: c })} testId="nfe-toma-pais" /></div>
                        <div><Label className="text-xs">Cidade</Label><Input disabled={readOnly} value={form.toma_extra?.endereco?.cidade_exterior || ""} onChange={(e) => setTomaEnd({ tipo: "exterior", cidade_exterior: e.target.value })} data-testid="nfe-toma-cidade-ext" /></div>
                        <div><Label className="text-xs">Estado/Província</Label><Input disabled={readOnly} value={form.toma_extra?.endereco?.estado_provincia_exterior || ""} onChange={(e) => setTomaEnd({ tipo: "exterior", estado_provincia_exterior: e.target.value })} data-testid="nfe-toma-estado-ext" /></div>
                        <div><Label className="text-xs">Código postal</Label><Input disabled={readOnly} value={form.toma_extra?.endereco?.codigo_postal_exterior || ""} onChange={(e) => setTomaEnd({ tipo: "exterior", codigo_postal_exterior: e.target.value })} data-testid="nfe-toma-cep-ext" /></div>
                      </>
                    ) : (
                      <>
                        <div><Label className="text-xs">Município</Label><MunicipioCombobox value={form.toma_extra?.endereco?.codigo_municipio_ibge || ""} onChange={(m) => setTomaEnd({ tipo: "nacional", codigo_municipio_ibge: m.codigo_ibge })} testId="nfe-toma-municipio" /></div>
                        <div><Label className="text-xs">CEP</Label><Input disabled={readOnly} value={form.toma_extra?.endereco?.cep || ""} onChange={(e) => setTomaEnd({ tipo: "nacional", cep: e.target.value })} data-testid="nfe-toma-cep" /></div>
                      </>
                    )}
                    <div><Label className="text-xs">Logradouro</Label><Input disabled={readOnly} value={form.toma_extra?.endereco?.logradouro || ""} onChange={(e) => setTomaEnd({ logradouro: e.target.value })} data-testid="nfe-toma-logradouro" /></div>
                    <div><Label className="text-xs">Número</Label><Input disabled={readOnly} value={form.toma_extra?.endereco?.numero || ""} onChange={(e) => setTomaEnd({ numero: e.target.value })} data-testid="nfe-toma-numero" /></div>
                    <div><Label className="text-xs">Complemento</Label><Input disabled={readOnly} value={form.toma_extra?.endereco?.complemento || ""} onChange={(e) => setTomaEnd({ complemento: e.target.value })} data-testid="nfe-toma-complemento" /></div>
                    <div><Label className="text-xs">Bairro</Label><Input disabled={readOnly} value={form.toma_extra?.endereco?.bairro || ""} onChange={(e) => setTomaEnd({ bairro: e.target.value })} data-testid="nfe-toma-bairro" /></div>
                  </div>
                </Card>
              )}

              {/* ===================== INTERMEDIÁRIO ===================== */}
              {activeTab === "intermediario" && (
                <Card className="p-5 md:p-6">
                  <SectionHead icon={UserPlus} title="Intermediário" desc="Informe quando houver um intermediário na prestação do serviço." />
                  <div className="flex items-center gap-2 mb-3">
                    <Switch disabled={readOnly} checked={!!form.interm?.presente} onCheckedChange={(v) => setInterm({ presente: v })} data-testid="nfe-interm-presente" />
                    <Label className="text-sm">Há intermediário nesta prestação</Label>
                  </div>
                  {form.interm?.presente && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <Label className="text-xs">Documento</Label>
                        <Select disabled={readOnly} value={form.interm?.tipo_documento || "cnpj"} onValueChange={(v) => setInterm({ tipo_documento: v })}>
                          <SelectTrigger data-testid="nfe-interm-tipo-doc"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cnpj">CNPJ</SelectItem>
                            <SelectItem value="cpf">CPF</SelectItem>
                            <SelectItem value="nif">NIF (exterior)</SelectItem>
                            <SelectItem value="nao_informado">Não informado</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {form.interm?.tipo_documento === "cnpj" && <div><Label className="text-xs">CNPJ</Label><Input disabled={readOnly} value={form.interm?.cnpj || ""} onChange={(e) => setInterm({ cnpj: e.target.value })} data-testid="nfe-interm-cnpj" /></div>}
                      {form.interm?.tipo_documento === "cpf" && <div><Label className="text-xs">CPF</Label><Input disabled={readOnly} value={form.interm?.cpf || ""} onChange={(e) => setInterm({ cpf: e.target.value })} data-testid="nfe-interm-cpf" /></div>}
                      {form.interm?.tipo_documento === "nif" && <div><Label className="text-xs">NIF</Label><Input disabled={readOnly} value={form.interm?.nif || ""} onChange={(e) => setInterm({ nif: e.target.value })} data-testid="nfe-interm-nif" /></div>}
                      <div className="md:col-span-2"><Label className="text-xs">Nome/Razão social</Label><Input disabled={readOnly} value={form.interm?.xnome || ""} onChange={(e) => setInterm({ xnome: e.target.value })} data-testid="nfe-interm-xnome" /></div>
                      <div><Label className="text-xs">Inscrição Municipal</Label><Input disabled={readOnly} value={form.interm?.inscricao_municipal || ""} onChange={(e) => setInterm({ inscricao_municipal: e.target.value })} data-testid="nfe-interm-im" /></div>
                      <div><Label className="text-xs">Telefone</Label><Input disabled={readOnly} value={form.interm?.telefone || ""} onChange={(e) => setInterm({ telefone: e.target.value })} data-testid="nfe-interm-telefone" /></div>
                      <div><Label className="text-xs">E-mail</Label><Input disabled={readOnly} value={form.interm?.email || ""} onChange={(e) => setInterm({ email: e.target.value })} data-testid="nfe-interm-email" /></div>

                      <Separator className="md:col-span-3 my-1" />
                      <div className="md:col-span-3 text-xs font-medium text-slate-600">Endereço</div>
                      <div><Label className="text-xs">Município</Label><MunicipioCombobox value={form.interm?.endereco?.codigo_municipio_ibge || ""} onChange={(m) => setIntermEnd({ tipo: "nacional", codigo_municipio_ibge: m.codigo_ibge })} testId="nfe-interm-municipio" /></div>
                      <div><Label className="text-xs">CEP</Label><Input disabled={readOnly} value={form.interm?.endereco?.cep || ""} onChange={(e) => setIntermEnd({ tipo: "nacional", cep: e.target.value })} data-testid="nfe-interm-cep" /></div>
                      <div><Label className="text-xs">Logradouro</Label><Input disabled={readOnly} value={form.interm?.endereco?.logradouro || ""} onChange={(e) => setIntermEnd({ logradouro: e.target.value })} data-testid="nfe-interm-logradouro" /></div>
                    </div>
                  )}
                </Card>
              )}

              {/* ===================== SERVIÇO ===================== */}
              {activeTab === "servico" && (
                <Card className="p-5 md:p-6">
                  <SectionHead icon={Wrench} title="Dados do serviço" desc="Informe os dados do serviço prestado. Estas informações compõem a descrição da NFS-e." />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <Label className="text-xs">Local da prestação</Label>
                      <Select disabled={readOnly} value={form.local_prestacao_tipo} onValueChange={(v) => set({ local_prestacao_tipo: v })}>
                        <SelectTrigger data-testid="nfe-local-tipo"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nacional">Nacional</SelectItem>
                          <SelectItem value="exterior">Exterior</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {form.local_prestacao_tipo === "exterior" && (
                      <div><Label className="text-xs">País da prestação</Label><PaisCombobox value={form.pais_prestacao} onChange={(c) => set({ pais_prestacao: c })} testId="nfe-pais-prestacao" /></div>
                    )}
                  </div>
                  <div className="mb-4">
                    <Label className="text-xs">Município da prestação do serviço</Label>
                    <MunicipioCombobox value={form.codigo_municipio_ibge} info={info.codigo_municipio_ibge_info} onChange={(m) => set({ codigo_municipio_ibge: m.codigo_ibge })} testId="nfe-municipio" />
                    <p className="text-xs text-slate-400 mt-1">Busque por nome do município ou código do IBGE.</p>
                  </div>
                  <div className="mb-3">
                    <Label className="text-xs">Código de Tributação Nacional</Label>
                    <CodigoTributacaoNacionalCombobox value={form.codigo_servico_nacional} info={info.codigo_servico_nacional_info} onChange={(item) => set({ codigo_servico_nacional: item.codigo })} testId="nfe-cod-servico" />
                    {/* Item 3 — descrição longa do código selecionado exibida
                        por extenso, com quebra de linha, sem alargar o container. */}
                    {info.codigo_servico_nacional_info?.descricao && (
                      <div className="mt-2 rounded-lg border border-blue-100 bg-blue-50/60 p-3 flex items-start gap-2">
                        <FileText size={14} className="text-primary mt-0.5 shrink-0" />
                        <p className="text-xs text-slate-600 leading-snug break-words min-w-0">
                          {info.codigo_servico_nacional_info.descricao}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Parâmetros municipais (NFS-e Nacional) — item 9 do pedido:
                      bloco compacto, reaproveita este mesmo card, sem tela nova. */}
                  <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="nfe-municipais-card">
                    <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                        {municipaisLoading ? (
                          <><Clock size={13} className="animate-spin" /> Consultando parâmetros municipais…</>
                        ) : municipais?.verificado ? (
                          <><CheckCircle2 size={13} className="text-emerald-600" /> Parâmetros municipais: consultados</>
                        ) : (
                          <><AlertTriangle size={13} className="text-amber-600" /> Parâmetros municipais: não verificados</>
                        )}
                      </div>
                      {!readOnly && (
                        <button type="button" onClick={() => carregarParametrosMunicipais(true)} disabled={municipaisLoading}
                          className="text-xs text-primary hover:underline flex items-center gap-1 disabled:opacity-50" data-testid="nfe-municipais-refresh">
                          <RefreshCw size={12} /> Atualizar parâmetros
                        </button>
                      )}
                    </div>
                    {municipais?.verificado ? (
                      <div className="text-xs text-slate-600 space-y-0.5">
                        <div>Alíquota municipal: {municipais.resumo?.aliquota_percentual != null ? `${municipais.resumo.aliquota_percentual}%` : "não informada pela NFS-e Nacional"}</div>
                        <div>Retenção do ISSQN: {municipais.resumo?.retencao_issqn === true ? "Sim" : municipais.resumo?.retencao_issqn === false ? "Não" : "não informada pela NFS-e Nacional"}</div>
                        <div>Regime especial: {municipais.resumo?.regime_especial || "não informado pela NFS-e Nacional"}</div>
                        {municipais.fonte && <div className="text-slate-400">{municipais.fonte}</div>}
                        {municipais.somente_leitura && (
                          <div className="text-slate-400">Nota já emitida — parâmetros exibidos são os usados na emissão.</div>
                        )}
                        {!readOnly && municipais.resumo?.aliquota_percentual != null
                          && String(municipais.resumo.aliquota_percentual) !== String(form.aliquota_issqn) && (
                          <button type="button" data-testid="nfe-municipais-usar-aliquota"
                            onClick={() => set({ aliquota_issqn: municipais.resumo.aliquota_percentual })}
                            className="text-primary hover:underline mt-1">
                            Usar alíquota sugerida ({municipais.resumo.aliquota_percentual}%) no lugar de {form.aliquota_issqn || "não preenchida"} — depois é preciso salvar
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">{municipais?.motivo || "Selecione o município e o código de tributação nacional para consultar."}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                    <div><Label className="text-xs">Código Complementar Municipal (opcional)</Label><Input disabled={readOnly} value={form.codigo_tributacao_municipal || ""} onChange={(e) => set({ codigo_tributacao_municipal: e.target.value })} data-testid="nfe-cod-tribmun" /></div>
                    <div><Label className="text-xs">Código NBS (opcional)</Label><Input disabled={readOnly} value={form.codigo_nbs || ""} onChange={(e) => set({ codigo_nbs: e.target.value })} data-testid="nfe-nbs" /><p className="text-xs text-slate-400 mt-1">Código da Nomenclatura Brasileira de Serviços (opcional).</p></div>
                  </div>
                  <div>
                    <Label className="text-xs">Descrição do serviço (substitui o texto gerado da fatura)</Label>
                    <Textarea disabled={readOnly} rows={4} maxLength={2000} value={form.descricao_servico || ""} onChange={(e) => set({ descricao_servico: e.target.value })} data-testid="nfe-descricao" />
                    <div className="text-right text-xs text-slate-400 mt-1">{(form.descricao_servico || "").length} / 2.000</div>
                  </div>
                </Card>
              )}

              {/* ===================== TRIBUTAÇÃO ===================== */}
              {activeTab === "tributacao" && (
                <>
                  <Card className="p-5 md:p-6 space-y-4">
                    <SectionHead icon={Percent} title="Tributação do ISSQN" desc="Informe a forma de tributação e retenção do ISSQN para este serviço." />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <Label className="text-xs">Tributação do ISSQN</Label>
                        <Select disabled={readOnly} value={form.tributacao_issqn} onValueChange={(v) => set({ tributacao_issqn: v })}>
                          <SelectTrigger data-testid="nfe-tributacao"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="tributavel">Operação tributável</SelectItem>
                            <SelectItem value="imunidade">Imunidade</SelectItem>
                            <SelectItem value="exportacao">Exportação de serviço</SelectItem>
                            <SelectItem value="nao_incidencia">Não incidência</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {form.tributacao_issqn === "exportacao" && (
                        <div><Label className="text-xs">País de resultado da prestação</Label><PaisCombobox value={form.pais_resultado_exportacao} onChange={(c) => set({ pais_resultado_exportacao: c })} testId="nfe-pais-resultado" /></div>
                      )}
                      {form.tributacao_issqn === "imunidade" && (
                        <div>
                          <Label className="text-xs">Tipo de imunidade</Label>
                          <Select disabled={readOnly} value={form.tipo_imunidade ?? "__none"} onValueChange={(v) => set({ tipo_imunidade: v === "__none" ? null : v })}>
                            <SelectTrigger data-testid="nfe-tipo-imunidade"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">—</SelectItem>
                              <SelectItem value="1">Patrimônio/renda/serviços entre entes (CF Art. 150, VI, a)</SelectItem>
                              <SelectItem value="2">Templos de qualquer culto</SelectItem>
                              <SelectItem value="3">Partidos/entidades sindicais/educação/assistência social</SelectItem>
                              <SelectItem value="4">Livros, jornais, periódicos e papel de impressão</SelectItem>
                              <SelectItem value="5">Fonogramas/videofonogramas musicais nacionais</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div>
                        <Label className="text-xs">Retenção do ISSQN</Label>
                        <Select disabled={readOnly} value={form.retencao_issqn} onValueChange={(v) => set({ retencao_issqn: v })}>
                          <SelectTrigger data-testid="nfe-retencao"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="nao_retido">Não retido</SelectItem>
                            <SelectItem value="retido_tomador">Retido pelo tomador</SelectItem>
                            <SelectItem value="retido_intermediario">Retido pelo intermediário</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Alíquota do ISSQN (%)</Label><Input disabled={readOnly} type="number" step="0.01" value={form.aliquota_issqn} onChange={(e) => set({ aliquota_issqn: e.target.value })} data-testid="nfe-aliquota" /></div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Switch disabled={readOnly} checked={!!form.suspensao_exigibilidade} onCheckedChange={(v) => set({ suspensao_exigibilidade: v })} data-testid="nfe-suspensao-toggle" />
                      <Label className="text-sm">Suspensão da exigibilidade</Label>
                    </div>
                    {form.suspensao_exigibilidade && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <Label className="text-xs">Tipo</Label>
                          <Select disabled={readOnly} value={form.tipo_suspensao ?? "__none"} onValueChange={(v) => set({ tipo_suspensao: v === "__none" ? null : v })}>
                            <SelectTrigger data-testid="nfe-tipo-suspensao"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">—</SelectItem>
                              <SelectItem value="1">Decisão judicial</SelectItem>
                              <SelectItem value="2">Processo administrativo</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-xs">Número do processo (30 dígitos)</Label><Input disabled={readOnly} value={form.numero_processo_suspensao || ""} onChange={(e) => set({ numero_processo_suspensao: e.target.value })} data-testid="nfe-numero-processo" /></div>
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <Switch disabled={readOnly} checked={!!form.beneficio_municipal} onCheckedChange={(v) => set({ beneficio_municipal: v })} data-testid="nfe-beneficio-toggle" />
                      <Label className="text-sm">Benefício municipal</Label>
                    </div>
                    {form.beneficio_municipal && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <div><Label className="text-xs">Número do benefício (informado pelo município)</Label><Input disabled={readOnly} value={form.numero_beneficio_municipal || ""} onChange={(e) => set({ numero_beneficio_municipal: e.target.value })} data-testid="nfe-num-beneficio" /></div>
                        <div><Label className="text-xs">Redução da base — valor (R$)</Label><Input disabled={readOnly} type="number" step="0.01" value={centsToStr(form.valor_reducao_base_beneficio_centavos)} onChange={(e) => set({ valor_reducao_base_beneficio_centavos: strToCents(e.target.value), percentual_reducao_base_beneficio: "" })} data-testid="nfe-valor-reducao" /></div>
                        <div><Label className="text-xs">Redução da base — percentual (%)</Label><Input disabled={readOnly} type="number" step="0.01" value={form.percentual_reducao_base_beneficio} onChange={(e) => set({ percentual_reducao_base_beneficio: e.target.value, valor_reducao_base_beneficio_centavos: null })} data-testid="nfe-percentual-reducao" /></div>
                      </div>
                    )}
                  </Card>

                  <Card className="p-5 md:p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Switch disabled={readOnly} checked={!!form.tributacao_federal_presente} onCheckedChange={(v) => set({ tributacao_federal_presente: v })} data-testid="nfe-federal-toggle" />
                      <Label className="text-sm font-medium">Tributação federal (PIS/COFINS/CSLL/IRRF/CP)</Label>
                    </div>
                    {form.tributacao_federal_presente && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div><Label className="text-xs">CST PIS/COFINS</Label><Input disabled={readOnly} value={form.pis_cofins_cst || ""} onChange={(e) => set({ pis_cofins_cst: e.target.value })} data-testid="nfe-cst" /></div>
                        <div><Label className="text-xs">Alíquota PIS (%)</Label><Input disabled={readOnly} type="number" step="0.01" value={form.pis_cofins_aliquota_pis} onChange={(e) => set({ pis_cofins_aliquota_pis: e.target.value })} data-testid="nfe-aliq-pis" /></div>
                        <div><Label className="text-xs">Alíquota COFINS (%)</Label><Input disabled={readOnly} type="number" step="0.01" value={form.pis_cofins_aliquota_cofins} onChange={(e) => set({ pis_cofins_aliquota_cofins: e.target.value })} data-testid="nfe-aliq-cofins" /></div>
                        <div><Label className="text-xs">Base PIS/COFINS (R$)</Label><Input disabled={readOnly} type="number" step="0.01" value={centsToStr(form.pis_cofins_base_centavos)} onChange={(e) => set({ pis_cofins_base_centavos: strToCents(e.target.value) })} data-testid="nfe-base-piscofins" /></div>
                        <div><Label className="text-xs">Valor PIS (R$)</Label><Input disabled={readOnly} type="number" step="0.01" value={centsToStr(form.pis_cofins_valor_pis_centavos)} onChange={(e) => set({ pis_cofins_valor_pis_centavos: strToCents(e.target.value) })} data-testid="nfe-valor-pis" /></div>
                        <div><Label className="text-xs">Valor COFINS (R$)</Label><Input disabled={readOnly} type="number" step="0.01" value={centsToStr(form.pis_cofins_valor_cofins_centavos)} onChange={(e) => set({ pis_cofins_valor_cofins_centavos: strToCents(e.target.value) })} data-testid="nfe-valor-cofins" /></div>
                        <div><Label className="text-xs">Retenção CP (R$)</Label><Input disabled={readOnly} type="number" step="0.01" value={centsToStr(form.valor_retencao_cp_centavos)} onChange={(e) => set({ valor_retencao_cp_centavos: strToCents(e.target.value) })} data-testid="nfe-ret-cp" /></div>
                        <div><Label className="text-xs">Retenção IRRF (R$)</Label><Input disabled={readOnly} type="number" step="0.01" value={centsToStr(form.valor_retencao_irrf_centavos)} onChange={(e) => set({ valor_retencao_irrf_centavos: strToCents(e.target.value) })} data-testid="nfe-ret-irrf" /></div>
                        <div><Label className="text-xs">Retenção CSLL (R$)</Label><Input disabled={readOnly} type="number" step="0.01" value={centsToStr(form.valor_retencao_csll_centavos)} onChange={(e) => set({ valor_retencao_csll_centavos: strToCents(e.target.value) })} data-testid="nfe-ret-csll" /></div>
                      </div>
                    )}
                    <p className="text-xs text-slate-500 mt-3 flex items-start gap-1.5">
                      <Info size={13} className="mt-0.5 shrink-0" />
                      Campos informados manualmente — o LOOTUS não calcula automaticamente a composição de tributos
                      federais nesta fase.
                    </p>
                  </Card>

                  <Card className="p-5 md:p-6">
                    <SectionHead icon={Info} title="IBS/CBS" desc="Grupo da Reforma Tributária do Consumo — fora de escopo nesta fase." />
                    <p className="text-xs text-slate-500 flex items-start gap-2">
                      <Info size={14} className="mt-0.5 shrink-0" />
                      Grupo da Reforma Tributária do Consumo (EC 132/2023) — opcional no XSD vigente e com regras de
                      obrigatoriedade ainda em Produção Restrita (Nota Técnica SE/CGNFS-e nº 004), não confirmadas em
                      Produção. Mantido de fora desta entrega para não implementar campos ainda não exigíveis.
                    </p>
                  </Card>
                </>
              )}

              {/* ===================== VALORES ===================== */}
              {activeTab === "valores" && (
                <Card className="p-5 md:p-6">
                  <SectionHead icon={DollarSign} title="Valores" desc="Valor do serviço, descontos e valores acessórios da nota." />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div><Label className="text-xs">Valor do serviço (da fatura)</Label><div className="mt-1.5 font-semibold">{money(valorServico)}</div></div>
                    <div><Label className="text-xs">Desconto incondicionado (R$)</Label><Input disabled={readOnly} type="number" step="0.01" min="0" value={centsToStr(form.desconto_incondicionado_centavos)} onChange={(e) => set({ desconto_incondicionado_centavos: strToCents(e.target.value) })} data-testid="nfe-desc-incond" /></div>
                    <div><Label className="text-xs">Desconto condicionado (R$)</Label><Input disabled={readOnly} type="number" step="0.01" min="0" value={centsToStr(form.desconto_condicionado_centavos)} onChange={(e) => set({ desconto_condicionado_centavos: strToCents(e.target.value) })} data-testid="nfe-desc-cond" /></div>
                    {form.interm?.presente && (
                      <div><Label className="text-xs">Valor recebido pelo intermediário (R$)</Label><Input disabled={readOnly} type="number" step="0.01" min="0" value={centsToStr(form.valor_recebido_intermediario_centavos)} onChange={(e) => set({ valor_recebido_intermediario_centavos: strToCents(e.target.value) })} data-testid="nfe-valor-receb-interm" /></div>
                    )}
                  </div>

                  <Separator className="my-5" />

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div><Label className="text-xs">Valor do serviço</Label><div className="mt-1.5 font-semibold">{money(valorServico)}</div></div>
                    <div><Label className="text-xs">Descontos</Label><div className="mt-1.5">{money(descontoIncond + descontoCond)}</div></div>
                    <div><Label className="text-xs">ISSQN estimado (se alíquota informada)</Label><div className="mt-1.5">{valorIssqnEstimado !== null ? money(valorIssqnEstimado) : "—"}</div></div>
                    <div><Label className="text-xs">Valor líquido estimado</Label><div className="mt-1.5 font-semibold">{money(valorLiquido)}</div></div>
                  </div>
                </Card>
              )}

              {/* ===================== COMPLEMENTARES ===================== */}
              {activeTab === "complementares" && (
                <>
                  <Card className="p-5 md:p-6">
                    <SectionHead icon={FileText} title="Informações complementares" desc="Documentos de referência e observações livres da nota." />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div><Label className="text-xs">Documento de Responsabilidade Técnica (ART/RRT/DRT)</Label><Input disabled={readOnly} value={form.doc_responsabilidade_tecnica || ""} onChange={(e) => set({ doc_responsabilidade_tecnica: e.target.value })} data-testid="nfe-doctec" /></div>
                      <div><Label className="text-xs">Documento de referência</Label><Input disabled={readOnly} value={form.documento_referencia || ""} onChange={(e) => set({ documento_referencia: e.target.value })} data-testid="nfe-docref" /></div>
                      <div><Label className="text-xs">Número do pedido/ordem de compra/serviço</Label><Input disabled={readOnly} value={form.numero_pedido || ""} onChange={(e) => set({ numero_pedido: e.target.value })} data-testid="nfe-numped" /></div>
                    </div>
                    <div className="mt-3">
                      <Label className="text-xs">Informações complementares (livre)</Label>
                      <Textarea disabled={readOnly} rows={3} maxLength={2000} value={form.informacoes_complementares || ""} onChange={(e) => set({ informacoes_complementares: e.target.value })} data-testid="nfe-infocompl" />
                    </div>
                  </Card>

                  <Card className="p-5 md:p-6">
                    <SectionHead icon={Layers} title="Situações especiais" desc="Comércio exterior (importação/exportação de serviço)." />
                    <div className="flex items-center gap-2 mb-3">
                      <Switch disabled={readOnly} checked={!!form.comex_presente} onCheckedChange={(v) => set({ comex_presente: v })} data-testid="nfe-comex-toggle" />
                      <Label className="text-sm">Comércio exterior (importação/exportação de serviço)</Label>
                    </div>
                    {form.comex_presente && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <Label className="text-xs">Modo de prestação</Label>
                          <Select disabled={readOnly} value={form.comex_modo_prestacao ?? "__none"} onValueChange={(v) => set({ comex_modo_prestacao: v === "__none" ? null : v })}>
                            <SelectTrigger data-testid="nfe-comex-modo"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">—</SelectItem>
                              <SelectItem value="1">Transfronteiriço</SelectItem>
                              <SelectItem value="2">Consumo no Brasil</SelectItem>
                              <SelectItem value="3">Movimento temporário de pessoas físicas</SelectItem>
                              <SelectItem value="4">Consumo no exterior</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Vínculo entre as partes</Label>
                          <Select disabled={readOnly} value={form.comex_vinculo_prestacao ?? "__none"} onValueChange={(v) => set({ comex_vinculo_prestacao: v === "__none" ? null : v })}>
                            <SelectTrigger data-testid="nfe-comex-vinculo"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">—</SelectItem>
                              <SelectItem value="0">Sem vínculo</SelectItem>
                              <SelectItem value="1">Controlada</SelectItem>
                              <SelectItem value="2">Controladora</SelectItem>
                              <SelectItem value="3">Coligada</SelectItem>
                              <SelectItem value="4">Matriz</SelectItem>
                              <SelectItem value="5">Filial ou sucursal</SelectItem>
                              <SelectItem value="6">Outro vínculo</SelectItem>
                              <SelectItem value="9">Desconhecido</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-xs">Moeda (código BACEN)</Label><Input disabled={readOnly} value={form.comex_moeda || ""} onChange={(e) => set({ comex_moeda: e.target.value })} data-testid="nfe-comex-moeda" /></div>
                        <div><Label className="text-xs">Valor do serviço na moeda</Label><Input disabled={readOnly} type="number" step="0.01" value={centsToStr(form.comex_valor_servico_moeda_centavos)} onChange={(e) => set({ comex_valor_servico_moeda_centavos: strToCents(e.target.value) })} data-testid="nfe-comex-valor" /></div>
                        <div><Label className="text-xs">Mecanismo fomento — prestador</Label><Input disabled={readOnly} value={form.comex_mecanismo_fomento_prestador || ""} onChange={(e) => set({ comex_mecanismo_fomento_prestador: e.target.value })} data-testid="nfe-comex-mec-prest" /></div>
                        <div><Label className="text-xs">Mecanismo fomento — tomador</Label><Input disabled={readOnly} value={form.comex_mecanismo_fomento_tomador || ""} onChange={(e) => set({ comex_mecanismo_fomento_tomador: e.target.value })} data-testid="nfe-comex-mec-toma" /></div>
                        <div>
                          <Label className="text-xs">Movimentação temporária de bens</Label>
                          <Select disabled={readOnly} value={form.comex_movimentacao_temp_bens ?? "__none"} onValueChange={(v) => set({ comex_movimentacao_temp_bens: v === "__none" ? null : v })}>
                            <SelectTrigger data-testid="nfe-comex-movtemp"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">—</SelectItem>
                              <SelectItem value="1">Não</SelectItem>
                              <SelectItem value="2">Vinculada — Declaração de Importação</SelectItem>
                              <SelectItem value="3">Vinculada — Declaração de Exportação</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Compartilhar com MDIC</Label>
                          <Select disabled={readOnly} value={form.comex_enviar_mdic ?? "__none"} onValueChange={(v) => set({ comex_enviar_mdic: v === "__none" ? null : v })}>
                            <SelectTrigger data-testid="nfe-comex-mdic"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">—</SelectItem>
                              <SelectItem value="0">Não</SelectItem>
                              <SelectItem value="1">Sim</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-xs">Nº Declaração de Importação (opcional)</Label><Input disabled={readOnly} value={form.comex_numero_di || ""} onChange={(e) => set({ comex_numero_di: e.target.value })} data-testid="nfe-comex-di" /></div>
                        <div><Label className="text-xs">Nº Registro de Exportação (opcional)</Label><Input disabled={readOnly} value={form.comex_numero_re || ""} onChange={(e) => set({ comex_numero_re: e.target.value })} data-testid="nfe-comex-re" /></div>
                      </div>
                    )}
                    <p className="text-xs text-slate-500 mt-3 flex items-start gap-1.5">
                      <Info size={13} className="mt-0.5 shrink-0" />
                      Obra e Evento (atividades de construção civil/eventos artísticos-esportivos) não foram
                      implementados nesta entrega — fora do caso de uso típico dos tenants do LOOTUS (serviços de
                      TI/consultoria).
                    </p>
                  </Card>
                </>
              )}

              {/* ===================== REVISÃO ===================== */}
              {activeTab === "revisao" && (
                <Card className="p-5 md:p-6 space-y-5">
                  <SectionHead icon={ClipboardCheck} title="Revisão" desc="Confira o resumo e as pendências antes de validar e emitir a NFS-e." />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div><Label className="text-xs">Valor do serviço</Label><div className="font-semibold mt-1">{money(valorServico)}</div></div>
                    <div><Label className="text-xs">Descontos</Label><div className="mt-1">{money(descontoIncond + descontoCond)}</div></div>
                    <div><Label className="text-xs">ISSQN estimado (se alíquota informada)</Label><div className="mt-1">{valorIssqnEstimado !== null ? money(valorIssqnEstimado) : "—"}</div></div>
                    <div><Label className="text-xs">Valor líquido estimado</Label><div className="font-semibold mt-1">{money(valorLiquido)}</div></div>
                  </div>

                  <div className="grid grid-cols-1 gap-1.5">
                    {naoRevisao.map((t) => (
                      <div key={t.id} className="flex items-center justify-between text-sm py-1 border-b border-slate-100">
                        <div className="flex items-center gap-2">{statusIcon(tabStatusOf(t))} {t.nome}</div>
                        <button type="button" className="text-xs text-primary underline" onClick={() => setActiveTab(t.id)}>Editar</button>
                      </div>
                    ))}
                  </div>

                  {doc.validation_errors?.length > 0 && (
                    <div className="rounded-md bg-red-50 border border-red-200 p-3">
                      <div className="font-medium text-red-800 mb-1 text-sm">Pendências da validação (XSD + regras condicionais):</div>
                      <ul className="list-disc pl-5 text-red-700 text-xs space-y-0.5">
                        {doc.validation_errors.map((e, i) => <li key={i}>{e}</li>)}
                      </ul>
                    </div>
                  )}

                  <p className="text-[11px] text-slate-400">
                    [Emitir NFS-e] só fica tecnicamente habilitado quando o envio real à SEFIN Nacional for
                    confirmado — hoje ainda recusa com um aviso claro (gap oficial documentado), nunca simula sucesso.
                  </p>
                </Card>
              )}
            </div>

            {/* ===== RESUMO LATERAL (desktop) ===== */}
            <aside className="hidden xl:block">
              <div className="sticky top-0">{renderResumo()}</div>
            </aside>
          </div>
        </div>

        {/* ===== RODAPÉ FIXO ===== */}
        <div className="shrink-0 bg-white border-t border-slate-200 px-4 md:px-6 py-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Button variant="outline" onClick={() => navigate("/notas-fiscais")} data-testid="nfe-cancelar">
              {readOnly ? "Fechar" : "Cancelar"}
            </Button>
            <div className="hidden md:flex items-center gap-2 text-xs text-slate-400">
              <Lock size={13} /> Seus dados são protegidos e enviados com segurança para a prefeitura.
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {!readOnly && (
                <>
                  <Button variant="outline" onClick={() => salvar()} disabled={saving} data-testid="nfe-salvar">
                    {saving ? "Salvando..." : "Salvar rascunho"}
                  </Button>
                  <Button onClick={validar} disabled={validating} data-testid="nfe-validar">
                    <FileCheck2 size={14} className="mr-1" /> {validating ? "Validando..." : "Validar DPS"}
                  </Button>
                </>
              )}
              <Button
                onClick={emitir}
                disabled={!podeEmitir || emitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                data-testid="nfe-emitir"
              >
                <Send size={14} className="mr-1" /> {emitting ? "Emitindo..." : "Emitir NFS-e"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
